import { AxiosError } from "axios";
import httpClient from "helpers/hybridHttpClient";
import type { NextPageContext, GetServerSidePropsContext } from "next";
import {
  FetchQueryOptions,
  QueryClient,
  QueryFunctionContext,
  useQuery,
} from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import type { Feature } from "types/layout/feature";

const APPLICATION_NAME = `${process.env.APPLICATION_NAME}`;

interface LayoutFeatureKeyFactoryOptions {
  courseSlug: string;
}

export const generateLayoutFeaturesKey = (
  options: LayoutFeatureKeyFactoryOptions,
) => ["layoutFeatures", "list", options] as const;

type LayoutFeaturesQueryKey = ReturnType<typeof generateLayoutFeaturesKey>;

export const fetchLayoutFeatures = async (
  courseSlug: string,
  ctx?: GetServerSidePropsContext | NextPageContext,
) => {
  const { data } = await httpClient(ctx).get<Feature[]>(
    `/api/layout-user/feature/${courseSlug}/`,
  );

  return data;
};

const layoutFeaturesQueryFnFactory =
  (ctx?: GetServerSidePropsContext | NextPageContext) =>
  ({
    queryKey: [, , { courseSlug }],
  }: QueryFunctionContext<LayoutFeaturesQueryKey>) =>
    fetchLayoutFeatures(courseSlug, ctx);

const interpolateLocation = (location: string, courseSlug: string) => {
  return location.replace("[courseSlug]", courseSlug);
};

export const useLayoutFeatures = (courseSlug: string, enabled = true) => {
  const router = useRouter();
  const { t } = useTranslation();

  return useQuery({
    queryKey: generateLayoutFeaturesKey({ courseSlug }),
    queryFn: layoutFeaturesQueryFnFactory(),
    enabled,
    select: (layoutFeatures) => {
      const interpolatedLocationsFeatures = layoutFeatures.map(
        ({ location, ...feature }) => ({
          ...feature,
          location: interpolateLocation(location, courseSlug),
        }),
      );

      const hasActiveFeature = interpolatedLocationsFeatures.some(
        ({ location }) => location === router.asPath,
      );
      return interpolatedLocationsFeatures.map((feature, index) => {
        return {
          ...feature,
          title: t(`layout-features:${feature.i18n_title}`),
          interpolatedLocation: feature.location,
          isInternal: feature.owned_by_application === APPLICATION_NAME,
          section_i18n_title: feature.section_i18n_title
            ? t(`layout-features:${feature.section_i18n_title}`)
            : null,
          isActive:
            feature.location === router.asPath ||
            (!hasActiveFeature && index === 0),
        };
      });
    },
  });
};

useLayoutFeatures.generateKey = generateLayoutFeaturesKey;
useLayoutFeatures.fetchQuery = <TData = Feature[]>(
  queryClient: QueryClient,
  {
    courseSlug,
    ctx,
    ...options
  }: Omit<
    FetchQueryOptions<Feature[], AxiosError, TData, LayoutFeaturesQueryKey>,
    "queryFn" | "queryKey"
  > &
    LayoutFeatureKeyFactoryOptions & {
      ctx?: GetServerSidePropsContext | NextPageContext;
    },
) =>
  queryClient.fetchQuery({
    ...options,
    queryKey: generateLayoutFeaturesKey({ courseSlug }),
    queryFn: layoutFeaturesQueryFnFactory(ctx),
  });
